/**
 * Lets users drag the Zendesk launcher bubble out of the way, and opens the
 * conversation panel next to wherever the bubble was dropped.
 *
 * Two things make this awkward. The launcher lives in a cross-origin iframe, so
 * pointer events inside it never reach this document: we park a transparent
 * handle exactly on top of it, turn movement into a drag, and forward a press
 * that never moved to the widget through the zE JS API. And Zendesk owns the
 * inline styles of both frames, rewriting them on every state change: we place
 * the frames from a stylesheet rule instead, whose `!important` declarations
 * outrank their inline styles and can be dropped again without touching them.
 */

type ZendeskApi = (...args: unknown[]) => void;

declare global {
  interface Window {
    zE?: ZendeskApi;
  }
}

type Point = { x: number; y: number };
type Size = { width: number; height: number };
/** The panel edge that stays put while Zendesk animates its height. */
type Pin = "top" | "bottom";
/** Messaging and Web Widget (Classic) answer to different command names. */
type WidgetApi = "messenger" | "webWidget";
type Frame = "launcher" | "panel";

type Box = { rect: DOMRect; zIndex: number };

type DragState = {
  pointerId: number;
  origin: Point;
  grab: Point;
  size: Size;
  threshold: number;
  moved: boolean;
};

const LAUNCHER_SELECTOR =
  'iframe#launcher, iframe[title*="launch messaging" i]';
const PANEL_SELECTOR = 'iframe#webWidget, iframe[title="Messaging window" i]';

// Marker attributes rather than classes or ids: Zendesk rewrites those, but it
// has no reason to touch a data attribute it does not know about.
const FLAG: Record<Frame, string> = {
  launcher: "data-zendesk-draggable-launcher",
  panel: "data-zendesk-draggable-panel",
};
const STORAGE_KEY = "zendeskLauncherPosition";
const HANDLE_CSS = "position:fixed;background:transparent;touch-action:none;";

const EDGE_MARGIN = 8; // keeps a frame from being dropped half off-screen
const PANEL_GAP = 12; // breathing room between the bubble and the panel
const DRAG_THRESHOLD = 4; // px of travel before a mouse press counts as a drag
const TOUCH_DRAG_THRESHOLD = 10; // fingers wobble, so taps need more slack
const OPEN_CONFIRM_DELAY = 2500; // ms the widget gets to answer an open request

let initialized = false;
let launcher: HTMLIFrameElement | null = null;
let panel: HTMLIFrameElement | null = null;
let handle: HTMLDivElement | null = null;
let pageObserver: MutationObserver | null = null;
let widgetObserver: MutationObserver | null = null;
let rules: Record<Frame, CSSStyleRule> | null = null;
let position: Point | null = null;
let drag: DragState | null = null;
let syncQueued = false;
let widgetApi: WidgetApi | null = null;
let widgetApiWorks = false;
let openAttempts = 0;
let openWatchdog: number | undefined;
let disabled = false;

// Last CSS written per target, so an unchanged pass costs no style recalc.
const written: Record<Frame | "handle", string> = {
  launcher: "",
  panel: "",
  handle: "",
};

/**
 * Safe to call before the Zendesk snippet has finished loading: the widget
 * frames are picked up from a DOM observer whenever they show up (or are
 * re-created).
 */
export function initZendeskDraggable(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  position = readStoredPosition();
  createRules();
  findFrames();

  if (!launcher) watchPage();
  window.addEventListener("resize", scheduleSync);
}

/**
 * Zendesk mounts asynchronously, so the page-wide search stays armed until the
 * launcher turns up — from then on only Zendesk's own container is watched,
 * which keeps this off the path of every render the app does.
 */
function watchPage() {
  if (pageObserver) return;
  pageObserver = new MutationObserver(findFrames);
  pageObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

/* -------------------------------------------------------------------------- */
/* Widget frames                                                              */
/* -------------------------------------------------------------------------- */

function findFrames() {
  const nextLauncher =
    document.querySelector<HTMLIFrameElement>(LAUNCHER_SELECTOR);
  const nextPanel = document.querySelector<HTMLIFrameElement>(PANEL_SELECTOR);
  if (nextLauncher === launcher && nextPanel === panel) return;

  launcher = nextLauncher;
  panel = nextPanel;
  watchFrames();
  scheduleSync();
}

function watchFrames() {
  widgetObserver?.disconnect();
  widgetObserver = new MutationObserver(onWidgetMutation);

  for (const frame of [launcher, panel]) {
    if (!frame) continue;
    widgetObserver.observe(frame, {
      attributes: true,
      attributeFilter: ["style", "class", "hidden"],
    });
    // Frames are shown and hidden with animations, which move the computed
    // opacity without touching a single attribute; the observer misses those.
    frame.addEventListener("animationend", scheduleSync);
    frame.addEventListener("transitionend", scheduleSync);
  }

  // Zendesk's own container, holding both frames: the launcher's outermost
  // ancestor below <body>. The panel is built on first open and can be replaced
  // later, so watch that subtree for it instead of the whole page.
  let root: HTMLElement | null = launcher;
  while (root?.parentElement && root.parentElement !== document.body) {
    root = root.parentElement;
  }
  if (!root) {
    // Zendesk can tear its container down; without a launcher there is nothing
    // to watch, so fall back to the page until one turns up again.
    watchPage();
    return;
  }

  widgetObserver.observe(root, { childList: true, subtree: true });
  pageObserver?.disconnect();
  pageObserver = null;
}

function onWidgetMutation(records: MutationRecord[]) {
  // Only a changed child list can mean a frame was replaced; attribute changes
  // just mean the widget restyled itself.
  if (records.some((record) => record.type === "childList")) findFrames();
  scheduleSync();
}

/**
 * Zendesk builds the panel on first open, somewhere we cannot predict — it does
 * not always land in the container the launcher sits in. So while the panel is
 * unknown, every sync doubles as a search for it; once found this stops.
 */
function findPanel() {
  if (panel) return;
  panel = document.querySelector<HTMLIFrameElement>(PANEL_SELECTOR);
  if (panel) watchFrames();
}

/* -------------------------------------------------------------------------- */
/* Placement                                                                  */
/* -------------------------------------------------------------------------- */

function createRules() {
  const element = document.createElement("style");
  element.id = "zendesk-draggable-styles";
  document.head.appendChild(element);

  const sheet = element.sheet;
  if (!sheet) return;

  // Both rules start out matching nothing; a frame opts in by getting flagged.
  sheet.insertRule(`[${FLAG.launcher}] {}`, 0);
  sheet.insertRule(`[${FLAG.panel}] {}`, 1);
  rules = {
    launcher: sheet.cssRules[0] as CSSStyleRule,
    panel: sheet.cssRules[1] as CSSStyleRule,
  };
}

/**
 * Pins a frame to viewport coordinates, outranking Zendesk's inline styles.
 *
 * `pin` picks the edge that stays put. Zendesk opens and closes the panel by
 * animating its height, so holding the edge nearest the bubble is what makes it
 * grow out of, and collapse back towards, the bubble.
 */
function place(target: Frame, point: Point, size?: Size, pin: Pin = "top") {
  const vertical =
    pin === "bottom" && size
      ? `bottom:${window.innerHeight - point.y - size.height}px!important;top:auto!important`
      : `top:${point.y}px!important;bottom:auto!important`;
  const css = `position:fixed!important;margin:0!important;left:${point.x}px!important;right:auto!important;${vertical}`;

  if (rules && css !== written[target]) {
    written[target] = css;
    rules[target].style.cssText = css;
  }

  const frame = target === "launcher" ? launcher : panel;
  if (frame && !frame.hasAttribute(FLAG[target])) {
    frame.setAttribute(FLAG[target], "");
  }
}

function clampToViewport(point: Point, size: Size): Point {
  const maxX = Math.max(
    EDGE_MARGIN,
    window.innerWidth - size.width - EDGE_MARGIN
  );
  const maxY = Math.max(
    EDGE_MARGIN,
    window.innerHeight - size.height - EDGE_MARGIN
  );
  return {
    x: Math.min(Math.max(point.x, EDGE_MARGIN), maxX),
    y: Math.min(Math.max(point.y, EDGE_MARGIN), maxY),
  };
}

/** A frame's geometry, or null when Zendesk has it hidden. */
function measure(frame: HTMLIFrameElement): Box | null {
  const style = window.getComputedStyle(frame);
  if (
    style.visibility === "hidden" ||
    style.display === "none" ||
    style.opacity === "0"
  ) {
    return null;
  }
  const rect = frame.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  return { rect, zIndex: Number.parseInt(style.zIndex, 10) || 999998 };
}

/* -------------------------------------------------------------------------- */
/* Sync                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Coalesced to one pass per frame: Zendesk fires a burst of restyles per state
 * change, and a pointer can report faster than the display refreshes.
 */
function scheduleSync() {
  if (syncQueued) return;
  syncQueued = true;
  window.requestAnimationFrame(() => {
    syncQueued = false;
    findPanel();
    // Measured once here, since both halves work off the bubble's box.
    const box = !disabled && launcher ? measure(launcher) : null;
    syncLauncher(box);
    syncPanel(box);
  });
}

function syncLauncher(box: Box | null) {
  if (disabled || !launcher) return;
  const element = ensureHandle();

  // Zendesk fades the bubble out on some widget states, so the handle has to get
  // out of the way with it.
  if (!box) {
    writeHandleCss(element, `${HANDLE_CSS}display:none`);
    return;
  }

  const { rect, zIndex } = box;
  if (position) {
    // Re-clamped every pass, so a spot saved on a bigger screen — or stranded by
    // a resize — walks back on-screen by itself.
    position = clampToViewport(position, rect);
    place("launcher", position);
  }

  // Read back from `position` rather than the frame: the rule above has just
  // moved it, and re-measuring would both lag a frame and force a layout.
  const left = position?.x ?? rect.left;
  const top = position?.y ?? rect.top;
  writeHandleCss(
    element,
    `${HANDLE_CSS}display:block;cursor:${drag?.moved ? "grabbing" : "grab"};` +
      `left:${left}px;top:${top}px;width:${rect.width}px;height:${rect.height}px;z-index:${zIndex + 1}`
  );
}

/** Opens the conversation panel against whichever edge the bubble is nearest. */
function syncPanel(box: Box | null) {
  // Nothing to anchor to while the bubble still sits where Zendesk put it or is
  // hidden.
  if (!panel || !position || disabled || !box) {
    panel?.removeAttribute(FLAG.panel);
    return;
  }

  // Closing is an animation, and this runs the moment it starts: re-placing the
  // panel now would measure a frame collapsing to nothing, and dropping the
  // placement would send it back to Zendesk's corner to finish closing there.
  // Either way the panel has to see the animation out where it stands.
  if (!isPanelShown()) return;

  const size = panelSize(panel);
  // On small screens the panel goes full-screen; leave that layout alone.
  if (size.width >= window.innerWidth - 2 * EDGE_MARGIN) {
    panel.removeAttribute(FLAG.panel);
    return;
  }

  const spot = anchorToLauncher(box.rect, size);
  place("panel", clampToViewport(spot, size), size, spot.pin);
}

/**
 * Where the panel should sit so that it reads as belonging to the bubble, and
 * which of its edges to hold still while it grows and shrinks.
 */
function anchorToLauncher(rect: DOMRect, size: Size): Point & { pin: Pin } {
  // Side of the bubble the panel can grow towards without leaving the screen.
  const alignedLeft =
    rect.left + rect.width / 2 < window.innerWidth / 2
      ? rect.left
      : rect.right - size.width;

  // Sitting above the bubble, the edge nearest it is the panel's bottom one.
  const above = rect.top - PANEL_GAP - size.height;
  if (above >= EDGE_MARGIN) return { x: alignedLeft, y: above, pin: "bottom" };

  const below = rect.bottom + PANEL_GAP;
  if (below + size.height <= window.innerHeight - EDGE_MARGIN) {
    return { x: alignedLeft, y: below, pin: "top" };
  }

  // A panel taller than the room above and below would have to cover the bubble,
  // so put it alongside instead, on whichever side has room for it.
  const fitsRight =
    rect.right + PANEL_GAP + size.width <= window.innerWidth - EDGE_MARGIN;
  return {
    x: fitsRight ? rect.right + PANEL_GAP : rect.left - PANEL_GAP - size.width,
    y: rect.top + rect.height / 2 - size.height / 2,
    pin: "bottom",
  };
}

/* -------------------------------------------------------------------------- */
/* Panel state                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A pixel length capped by its maximum, falling back to the measured size when
 * either is a keyword such as `none` or `auto`.
 */
function toPx(length: string, max: string, fallback: number): number {
  const value = Number.parseFloat(length);
  const limit = Number.parseFloat(max);
  const capped = Math.min(
    Number.isNaN(value) ? Infinity : value,
    Number.isNaN(limit) ? Infinity : limit
  );
  return Number.isFinite(capped) && capped > 0 ? capped : fallback;
}

/**
 * The size the panel is animating towards. Its rect is useless mid-animation,
 * but the inline width and height Zendesk writes are already the final values.
 */
function panelSize(frame: HTMLIFrameElement): Size {
  const rect = frame.getBoundingClientRect();
  const style = window.getComputedStyle(frame);
  return {
    width: toPx(frame.style.width, style.maxWidth, rect.width),
    height: toPx(frame.style.height, style.maxHeight, rect.height),
  };
}

/** Whether Zendesk is animating the panel towards being visible. */
function isPanelShown(): boolean {
  if (!panel) return false;
  const style = window.getComputedStyle(panel);
  if (style.visibility === "hidden" || style.display === "none") return false;
  // Opening and closing is an opacity transition, so the computed value lies for
  // as long as it runs; the inline one Zendesk sets is the target state.
  return (panel.style.opacity || style.opacity) !== "0";
}

/**
 * Whether the panel is actually on screen. Deliberately stricter than
 * `isPanelShown`: reading a closed panel as open makes a press send `close`,
 * which looks to the user like the widget refusing to open.
 */
function isPanelOpen(): boolean {
  if (!panel || !isPanelShown()) return false;
  const rect = panel.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/* -------------------------------------------------------------------------- */
/* Drag handle                                                                */
/* -------------------------------------------------------------------------- */

function ensureHandle(): HTMLDivElement {
  if (handle) return handle;

  handle = document.createElement("div");
  handle.id = "zendesk-drag-handle";
  handle.setAttribute("aria-hidden", "true");
  handle.addEventListener("pointerdown", onPointerDown);
  handle.addEventListener("pointermove", onPointerMove);
  handle.addEventListener("pointerup", onPointerUp);
  handle.addEventListener("pointercancel", endDrag);
  document.body.appendChild(handle);
  return handle;
}

function writeHandleCss(element: HTMLDivElement, css: string) {
  if (css === written.handle) return;
  written.handle = css;
  element.style.cssText = css;
}

function onPointerDown(event: PointerEvent) {
  if (!launcher || (event.pointerType === "mouse" && event.button !== 0))
    return;

  const rect = launcher.getBoundingClientRect();
  drag = {
    pointerId: event.pointerId,
    origin: { x: event.clientX, y: event.clientY },
    grab: { x: event.clientX - rect.left, y: event.clientY - rect.top },
    size: { width: rect.width, height: rect.height },
    threshold:
      event.pointerType === "mouse" ? DRAG_THRESHOLD : TOUCH_DRAG_THRESHOLD,
    moved: false,
  };

  handle?.setPointerCapture(event.pointerId);
  event.preventDefault();
}

function onPointerMove(event: PointerEvent) {
  if (!drag || event.pointerId !== drag.pointerId) return;

  const travelled = Math.hypot(
    event.clientX - drag.origin.x,
    event.clientY - drag.origin.y
  );
  if (!drag.moved && travelled < drag.threshold) return;

  drag.moved = true;
  position = clampToViewport(
    { x: event.clientX - drag.grab.x, y: event.clientY - drag.grab.y },
    drag.size
  );
  scheduleSync();
}

function onPointerUp(event: PointerEvent) {
  if (!drag || event.pointerId !== drag.pointerId) return;

  const wasDragged = drag.moved;
  endDrag(event);

  if (wasDragged) {
    storePosition();
    return;
  }
  // A press that never moved is what the launcher itself would have treated as a
  // click, so hand it back to the widget.
  toggleWidget();
}

function endDrag(event: PointerEvent) {
  if (!drag || event.pointerId !== drag.pointerId) return;
  if (handle?.hasPointerCapture(event.pointerId)) {
    handle.releasePointerCapture(event.pointerId);
  }
  drag = null;
  scheduleSync();
}

/* -------------------------------------------------------------------------- */
/* Widget API                                                                 */
/* -------------------------------------------------------------------------- */

function apiName(): WidgetApi {
  if (widgetApi) return widgetApi;
  // Messaging titles its frames "…messaging window", Classic does not.
  const titles = `${launcher?.title ?? ""} ${panel?.title ?? ""}`;
  widgetApi = /messaging/i.test(titles) ? "messenger" : "webWidget";
  return widgetApi;
}

function toggleWidget() {
  const zE = window.zE;
  if (typeof zE !== "function") return;

  if (isPanelOpen()) {
    // The panel being open is proof that this API name reaches the widget, and
    // the pending watchdog would otherwise read the close as a failed open.
    widgetApiWorks = true;
    window.clearTimeout(openWatchdog);
    zE(apiName(), "close");
    return;
  }

  openAttempts = 0;
  requestOpen(zE);
}

/**
 * Calling the other flavour's API only logs an error, so an open request can
 * silently do nothing. The first open has to build the panel and is far from
 * instant, so wait before calling it a failure, then try the other name — and if
 * that is silent too, get out of the way rather than leaving support unreachable.
 */
function requestOpen(zE: ZendeskApi) {
  zE(apiName(), "open");
  openAttempts += 1;
  if (widgetApiWorks) return;

  window.clearTimeout(openWatchdog);
  openWatchdog = window.setTimeout(() => {
    findPanel();
    if (isPanelOpen()) {
      widgetApiWorks = true;
    } else if (openAttempts < 2) {
      widgetApi = apiName() === "messenger" ? "webWidget" : "messenger";
      requestOpen(zE);
    } else {
      disableDragging();
    }
  }, OPEN_CONFIRM_DELAY);
}

/** Last resort: give the launcher back to Zendesk, untouched and clickable. */
function disableDragging() {
  disabled = true;
  handle?.remove();
  handle = null;
  launcher?.removeAttribute(FLAG.launcher);
  panel?.removeAttribute(FLAG.panel);
  console.warn(
    "Zendesk ignored the widget API, so the launcher is no longer draggable."
  );
}

/* -------------------------------------------------------------------------- */
/* Persistence                                                                */
/* -------------------------------------------------------------------------- */

function readStoredPosition(): Point | null {
  try {
    const { x, y } = JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) || "{}"
    );
    return typeof x === "number" && typeof y === "number" ? { x, y } : null;
  } catch {
    return null;
  }
}

function storePosition() {
  if (!position) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch (error) {
    console.warn("Failed to persist the Zendesk launcher position", error);
  }
}
